import Decimal from 'decimal.js';

import { StorageLocation, ItemConfiguration } from 'apis/picker-api/types';
import {
  CourierRestriction,
  Item,
  ItemBaseAttrs,
  ItemCommission,
  ItemCreatePayload,
  ItemUpdatePayload,
  NutritionSetting,
  OptionBinding,
  OptionBindingAttrs,
  Product,
  ProductInformationSettings,
  Translation,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { pickOptionBindingAttrs } from 'apis/restaurant-api/merchant-admin/menu2/utils';
import { ItemPricingsUpdatePayload } from 'apis/restaurant-api/merchant-admin/merchant/types';
import { VatCategory } from 'apis/vat-categories/types';
import {
  EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY,
  fromWeeklyScheduleInputEntriesToWeeklyTimeSpans,
  fromWeeklyTimeSpansToWeeklyScheduleInputEntries,
} from 'common/components/weekly-schedules';
import { isDefined } from 'common/utils';
import { initialVatFieldValue } from 'modules/menu/components/VatCategories';

import { FormValues as CommissionsFormValues } from './components/Item/ItemCommissions/ItemCommissionsFormSection';
import { getUniqueImageUrls } from './components/Item/utils';
import { FormValues, ValidFormValues } from './components/ItemDetailsForm';
import { FeedFormValues } from './components/ItemDetailsForm/types';
import {
  getDiscountPercentage,
  getPercentageDiscountPrice,
  updatePrimaryLanguageTranslation,
} from './components/utils';
import { isAgeRestriction } from '../utils';

const serializeEnabledDeliveryMethods = (values: ValidFormValues): DeliveryMethod[] => {
  const enabledDeliveryMethods: DeliveryMethod[] = [];

  if (values.enableEatin) {
    enabledDeliveryMethods.push('eatin');
  }

  if (values.enableTakeaway) {
    enabledDeliveryMethods.push('takeaway');
  }

  if (values.enableDelivery) {
    enabledDeliveryMethods.push('homedelivery');
  }

  return enabledDeliveryMethods;
};

const serializeCourierRestrictions = (values: ValidFormValues): CourierRestriction[] => {
  const courierRestrictions: CourierRestriction[] = [];

  if (values.disableBikeDelivery) {
    courierRestrictions.push('no_bikes');
  }

  if (values.disableCarDelivery) {
    courierRestrictions.push('no_cars');
  }

  if (values.disableMotorcycleDelivery) {
    courierRestrictions.push('no_motorcycles');
  }

  return courierRestrictions;
};

const getBasePrice = (values: ValidFormValues, currency: Currency) => {
  switch (values.discountType) {
    case 'relative':
      if (typeof values.discountPercentage !== 'number') {
        throw Error('discountType is percentage but percentage is not a number');
      }
      return getPercentageDiscountPrice(values.price, values.discountPercentage, currency);
    case 'absolute':
      return String(values.fixedDiscountPrice);
    case undefined:
      return String(values.price);
  }
};

const getInventoryMode = (values: ValidFormValues): Item['inventory_mode'] => {
  if (values.inventoryMode === 'binary') {
    return values.inStock ? null : 'forced_out_of_stock';
  }
  return values.inventoryMode;
};

const findTranslation = (translations: Translation[], language: Language) =>
  translations.find(t => t.lang === language)?.value || '';

const transformItemFormValuesToApiBaseAttrs = (
  values: ValidFormValues,
  settings: {
    currency: Currency;
    nutritionFactsFormat: 'text' | 'table';
  },
): ItemBaseAttrs => {
  const { currency, nutritionFactsFormat } = settings;

  return {
    alcohol_percentage: values.alcoholPercentage * 10,
    availability_period: {
      start: values.availabilityPeriod.start ? { $date: values.availabilityPeriod.start } : null,
      end: values.availabilityPeriod.end ? { $date: values.availabilityPeriod.end } : null,
    },
    barcode_gtin: values.barcodeGtin || null,
    baseprice: {
      amount: getBasePrice(values, currency),
      currency,
    },
    brand_id: values.brandId || null,
    courier_restrictions: serializeCourierRestrictions(values),
    caffeine_content:
      values?.caffeineContentValue && values?.caffeineContentUnits
        ? {
            value: values.caffeineContentValue,
            units: values.caffeineContentUnits,
          }
        : null,
    dietary_preferences: values.dietaryPreferences,
    discounted: values.discountType
      ? {
          discount_type: values.discountType,
          original_price: {
            amount: String(values.price),
            currency,
          },
        }
      : null,
    delivery_methods: serializeEnabledDeliveryMethods(values),
    deposit_permutation_id:
      values.weightType !== 'weighted-items' && values.depositOption ? { $oid: values.depositOption } : null,
    enabled: values.enabled,
    external_id: values.posId || null,
    is_bundle_offer: values.bundleOffer,
    inventory_mode: getInventoryMode(values),
    mandatory_warnings: values.mandatoryWarnings,
    merchant_sku: values.merchantSku || null,
    nutrition_values:
      nutritionFactsFormat === 'table' && values.nutritionValues
        ? values.nutritionValues
            .filter((value): value is { value: string; id: string } => !!value.value)
            .map(value => ({ amount: value.value, nutrition_value_id: value.id }))
        : [],
    nutrition_values_baseline: (nutritionFactsFormat === 'table' && values.nutritionValuesBaseline) || null,
    product_hierarchy_level_id: values.productHierarchyLevelId ?? null,
    source_config: values.sourceCountry
      ? {
          catalog_country: values.sourceCountry || null,
          field_config: {
            additives: values.sourceConfig?.additives,
            alcohol_percentage: values.sourceConfig?.alcoholPercentage,
            allergens: values.sourceConfig?.allergens,
            brand_id: values.sourceConfig?.brandId,
            description: values.sourceConfig?.description,
            dietary_preferences: values.sourceConfig?.dietaryPreferences,
            distributor_information: values.sourceConfig?.distributorInfo,
            images: values.sourceConfig?.image,
            ingredients: values.sourceConfig?.ingredients,
            mandatory_warnings: values.sourceConfig?.mandatoryWarnings,
            more_information: values.sourceConfig?.moreInformation,
            name: values.sourceConfig?.name,
            number_of_units: values.sourceConfig?.numberOfUnits,
            nutrition_facts: values.sourceConfig?.nutritionFields,
            nutrition_values_baseline: values.sourceConfig?.nutritionFields,
            nutrition_values: values.sourceConfig?.nutritionFields,
            country_of_origin: values.sourceConfig?.countryOfOrigin,
            display_unit: values.sourceConfig?.displayUnit,
            conditions_of_use_and_storage: values.sourceConfig?.usageInstructions,
            producer_information: values.sourceConfig?.producerInfo,
            volume_in_ml: values.sourceConfig?.volumeInMl,
            weight_in_grams: values.sourceConfig?.weightInGrams,
            product_hierarchy_level_id: values.sourceConfig?.productHierarchyLevelId,
          },
        }
      : null,
    user_requirements: values.userRequirement ? [values.userRequirement] : [],
    vat_percentage: String(values.vat.percentage),
    vat_category_code: (values.vat.type === 'categories' && values.vat.categoryCode) || null,
    weekly_availability:
      values.useWeeklyAvailability && values.weeklyAvailability.length > 0
        ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(values.weeklyAvailability)
        : null,
    weekly_visibility:
      values.useWeeklyVisibility && values.weeklyVisibility.length > 0
        ? fromWeeklyScheduleInputEntriesToWeeklyTimeSpans(values.weeklyVisibility)
        : null,
    number_of_units: values.weightType === 'pre-packaged' && values.numberOfUnits ? values.numberOfUnits : null,
    volume_in_ml: values.weightType === 'pre-packaged' && values.volumeInMl ? values.volumeInMl : null,
    weight_in_grams: values.weightType === 'pre-packaged' && values.weightInGrams ? values.weightInGrams : null,
    display_unit: values.displayUnit,
    max_quantity_per_purchase: values.maxQuantityPerPurchase || null,
    min_quantity_per_purchase: values.minQuantityPerPurchase || null,
    sell_by_weight_config:
      values.weightType === 'weighted-items' && values.sellByWeightInputType && values.gramsPerStep
        ? {
            grams_per_step: values.gramsPerStep,
            input_type: values.sellByWeightInputType,
          }
        : null,
    wolt_plus_only: !!values.isWoltPlusOnly,
  };
};

export const transformCommissionsApiToFormValues = (itemCommissions: ItemCommission[]): CommissionsFormValues => {
  const homedelivery = itemCommissions.find(commission => commission.delivery_method === 'homedelivery')?.value;
  const takeaway = itemCommissions.find(commission => commission.delivery_method === 'takeaway')?.value;
  const eatin = itemCommissions.find(commission => commission.delivery_method === 'eatin')?.value;
  return {
    homedeliveryRelativeCommission:
      homedelivery !== undefined ? new Decimal(homedelivery).times(100).toDecimalPlaces(2).toNumber() : undefined,
    takeawayRelativeCommission:
      takeaway !== undefined ? new Decimal(takeaway).times(100).toDecimalPlaces(2).toNumber() : undefined,
    eatinRelativeCommission:
      eatin !== undefined ? new Decimal(eatin).times(100).toDecimalPlaces(2).toNumber() : undefined,
  };
};

export const transformCommissionsFormValuesToApi = (formData: CommissionsFormValues): ItemPricingsUpdatePayload => {
  const { homedeliveryRelativeCommission, takeawayRelativeCommission, eatinRelativeCommission } = formData;
  return {
    homedelivery: homedeliveryRelativeCommission !== undefined ? homedeliveryRelativeCommission / 100 : null,
    takeaway: takeawayRelativeCommission !== undefined ? takeawayRelativeCommission / 100 : null,
    eatin: eatinRelativeCommission !== undefined ? eatinRelativeCommission / 100 : null,
  };
};

export const transformStorageLocationFormValueToApi = (formValues: ValidFormValues): StorageLocation => {
  const { location } = formValues;
  return !!location ? location : null;
};

export const transformImagesApiToFormValues = (
  item: Item,
): { type: 'existing-image'; imageUrl: string; id: string }[] => {
  return getUniqueImageUrls(item).map(imageUrl => ({ type: 'existing-image', imageUrl, id: imageUrl }));
};

export const transformItemApiToFormValues = (
  item: Item,
  settings: {
    alcoholWarningPermilleLimit?: number;
    productInformationSettings?: ProductInformationSettings;
    language: Language;
    categoryId: string | null;
    itemBalance?: number;
    vatCategories?: VatCategory[];
    commissions?: ItemCommission[];
    itemConfig?: ItemConfiguration;
    pickingAreaItemAttrs?: { pickingAreaId: string; pickingItemSequence: number | undefined };
  },
): FormValues => {
  const {
    language,
    categoryId,
    vatCategories,
    itemBalance,
    commissions = [],
    itemConfig,
    productInformationSettings,
    alcoholWarningPermilleLimit,
    pickingAreaItemAttrs,
  } = settings;

  const nutritionValues =
    productInformationSettings?.nutrition_values
      ?.sort((a, b) => a.table_row_number - b.table_row_number)
      .map(setting => {
        const nutritionValue = item.nutrition_values.find(itemValue => itemValue.nutrition_value_id === setting.id);
        if (!setting.ask_by_default && !nutritionValue) {
          return undefined;
        }
        const value = nutritionValue?.amount;
        return { id: setting.id, value };
      })
      .filter(isDefined) ?? [];

  const relativeCommissions = transformCommissionsApiToFormValues(commissions);

  const hasTobaccoTag = productInformationSettings?.product_hierarchy_levels
    ?.find(productHierarchyLevel => productHierarchyLevel.id === item.product_hierarchy_level_id)
    ?.tags.includes('tobacco');

  const hasHighAlcoholContent =
    typeof alcoholWarningPermilleLimit === 'number' && item.alcohol_percentage > alcoholWarningPermilleLimit;

  return {
    categoryId,
    name: findTranslation(item.name, language),
    productHierarchyLevelId: item.product_hierarchy_level_id || undefined,
    description: findTranslation(item.description, language),
    price: item.discounted ? Number(item.discounted.original_price.amount) : Number(item.baseprice.amount),
    discountType: item.discounted?.discount_type || undefined,
    fixedDiscountPrice: item.discounted ? Number(item.baseprice.amount) : undefined,
    dietaryPreferences: item.dietary_preferences || [],
    discountPercentage: item.discounted
      ? getDiscountPercentage(item.baseprice.amount, item.discounted.original_price.amount)
      : undefined,
    weightInGrams: item.weight_in_grams || undefined,
    volumeInMl: item.volume_in_ml || undefined,
    numberOfUnits: item.number_of_units || undefined,
    displayUnit: item.display_unit,
    vat: initialVatFieldValue(vatCategories, item.vat_category_code || undefined, Number(item.vat_percentage)),
    alcoholPercentage: item.alcohol_percentage
      ? new Decimal(item.alcohol_percentage).times(0.1).toDecimalPlaces(1).toNumber()
      : 0,
    caffeineContentValue: item.caffeine_content?.value,
    caffeineContentUnits: item.caffeine_content?.units,
    posId: item.external_id || '',
    barcodeGtin: item.barcode_gtin || '',
    merchantSku: item.merchant_sku || '',
    sourceCountry: (item.source_config && item.source_config.catalog_country) || undefined,
    sourceConfig: item.source_config
      ? {
          additives: item.source_config?.field_config.additives || undefined,
          alcoholPercentage: item.source_config?.field_config.alcohol_percentage || undefined,
          allergens: item.source_config?.field_config.allergens || undefined,
          brandId: item.source_config?.field_config.brand_id || undefined,
          distributorInfo: item.source_config?.field_config.distributor_information || undefined,
          description: item.source_config.field_config.description || undefined,
          image: item.source_config?.field_config.images || undefined,
          ingredients: item.source_config?.field_config.ingredients || undefined,
          name: item.source_config?.field_config.name || undefined,
          numberOfUnits: item.source_config?.field_config.number_of_units || undefined,
          nutritionFields:
            item.source_config?.field_config.nutrition_facts ||
            item.source_config?.field_config.nutrition_values_baseline ||
            item.source_config?.field_config.nutrition_values ||
            undefined,
          countryOfOrigin: item.source_config?.field_config.country_of_origin || undefined,
          displayUnit: item.source_config?.field_config.display_unit || undefined,
          usageInstructions: item.source_config?.field_config.conditions_of_use_and_storage || undefined,
          producerInfo: item.source_config?.field_config.producer_information || undefined,
          volumeInMl: item.source_config?.field_config.volume_in_ml || undefined,
          weightInGrams: item.source_config?.field_config.weight_in_grams || undefined,
          productHierarchyLevelId: item.source_config?.field_config.product_hierarchy_level_id || undefined,
          dietaryPreferences: item.source_config?.field_config.dietary_preferences || undefined,
          mandatoryWarnings: item.source_config?.field_config.mandatory_warnings || undefined,
          moreInformation: item.source_config?.field_config.more_information || undefined,
        }
      : undefined,
    image: transformImagesApiToFormValues(item),
    ingredients: findTranslation(item.ingredients, language),
    additives: findTranslation(item.additives, language),
    allergens: findTranslation(item.allergens, language),
    brandId: item.brand_id,
    mandatoryWarnings: item.mandatory_warnings || [],
    moreInformation: findTranslation(item.more_information, language),
    nutritionFacts: findTranslation(item.nutrition_facts, language),
    nutritionValues,
    nutritionValuesBaseline: item.nutrition_values_baseline || undefined,
    countryOfOrigin: findTranslation(item.country_of_origin, language),
    usageInstructions: findTranslation(item.conditions_of_use_and_storage, language),
    producerInfo: findTranslation(item.producer_information, language),
    distributorInfo: findTranslation(item.distributor_information, language),
    bundleOffer: item.is_bundle_offer,
    depositOption: item.deposit_permutation_id?.$oid || '',
    enabled: item.enabled,
    availabilityPeriod: {
      start: item.availability_period.start ? item.availability_period.start.$date : null,
      end: item.availability_period.end ? item.availability_period.end.$date : null,
    },
    useWeeklyAvailability: item.weekly_availability !== null,
    weeklyAvailability:
      item.weekly_availability !== null
        ? fromWeeklyTimeSpansToWeeklyScheduleInputEntries(item.weekly_availability)
        : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
    useWeeklyVisibility: item.weekly_visibility !== null,
    weeklyVisibility:
      item.weekly_visibility !== null
        ? fromWeeklyTimeSpansToWeeklyScheduleInputEntries(item.weekly_visibility)
        : [EMPTY_WEEKLY_SCHEDULE_INPUT_ENTRY],
    userRequirement: item.user_requirements.length > 0 ? item.user_requirements[0] : undefined,
    maxQuantityPerPurchase: item.max_quantity_per_purchase || undefined,
    minQuantityPerPurchase: item.min_quantity_per_purchase || undefined,
    enableEatin: item.delivery_methods.includes('eatin'),
    enableTakeaway: item.delivery_methods.includes('takeaway'),
    enableDelivery: item.delivery_methods.includes('homedelivery'),
    disableBikeDelivery: item.courier_restrictions.includes('no_bikes'),
    disableCarDelivery: item.courier_restrictions.includes('no_cars'),
    disableMotorcycleDelivery: item.courier_restrictions.includes('no_motorcycles'),
    isRestrictedItem: item.restrictions.length > 0,
    isWoltPlusOnly: item.wolt_plus_only,
    isOTCRestricted: item.restrictions.some(restriction => restriction.type === 'over_the_counter'),
    isAgeRestricted:
      item.restrictions.some(restriction => restriction.type === 'age_restricted') ||
      hasTobaccoTag ||
      hasHighAlcoholContent,
    ageLimit: item.restrictions.find(isAgeRestriction)?.age_limit || undefined,
    isDeliveryHandshakeRestricted: item.restrictions.some(restriction => restriction.type === 'delivery_handshake'),
    inventoryMode:
      item.inventory_mode && item.inventory_mode !== 'forced_out_of_stock' ? item.inventory_mode : 'binary',
    inStock: item.inventory_mode !== 'forced_out_of_stock',
    inventoryBalance: itemBalance,
    homedeliveryRelativeCommission: relativeCommissions.homedeliveryRelativeCommission,
    takeawayRelativeCommission: relativeCommissions.takeawayRelativeCommission,
    eatinRelativeCommission: relativeCommissions.eatinRelativeCommission,
    location: itemConfig?.storageLocation || undefined,
    pickingAreaId: pickingAreaItemAttrs?.pickingAreaId,
    pickingItemSequence: pickingAreaItemAttrs?.pickingItemSequence,
    weightType: !!item.sell_by_weight_config ? 'weighted-items' : 'pre-packaged',
    sellByWeightInputType: item.sell_by_weight_config?.input_type || undefined,
    gramsPerStep: item.sell_by_weight_config?.grams_per_step || undefined,
    optionBindings: item.option_bindings,
    externalInfoHyperlink:
      item.external_info_hyperlink !== null
        ? {
            hyperlink: item.external_info_hyperlink.hyperlink,
            text: findTranslation(item.external_info_hyperlink.text, language),
          }
        : undefined,
    returnPolicy: item.return_policy ?? null,
  };
};

export const transformItemFormValuesToCreateApi = (
  values: ValidFormValues,
  optionBindingAttrs: OptionBindingAttrs[],
  imageUrls: string[],
  settings: {
    language: Language;
    currency: Currency;
    nutritionFactsFormat: 'text' | 'table';
    menuId: string;
    externalInfoHyperlinkAllowed: boolean;
  },
): ItemCreatePayload => {
  const { language, currency, nutritionFactsFormat, menuId } = settings;

  const baseAttributes = transformItemFormValuesToApiBaseAttrs(values, {
    currency,
    nutritionFactsFormat,
  });
  return {
    ...baseAttributes,
    additives: values.additives ? [{ lang: language, value: values.additives, verified: true }] : [],
    allergens: values.allergens ? [{ lang: language, value: values.allergens, verified: true }] : [],
    conditions_of_use_and_storage: values.usageInstructions
      ? [{ lang: language, value: values.usageInstructions, verified: true }]
      : [],
    country_of_origin: values.countryOfOrigin
      ? [{ lang: language, value: values.countryOfOrigin, verified: true }]
      : [],
    deposit_permutation_id:
      values.weightType !== 'weighted-items' && values.depositOption ? { $oid: values.depositOption } : null,
    description: values.description ? [{ lang: language, value: values.description, verified: true }] : [],
    distributor_information: values.distributorInfo
      ? [{ lang: language, value: values.distributorInfo, verified: true }]
      : [],
    ingredients: values.ingredients ? [{ lang: language, value: values.ingredients, verified: true }] : [],
    is_bundle_offer: values.bundleOffer,
    more_information: values.moreInformation ? [{ lang: language, value: values.moreInformation, verified: true }] : [],
    name: [{ lang: language, value: values.name, verified: true }],
    nutrition_facts: values.nutritionFacts ? [{ lang: language, value: values.nutritionFacts, verified: true }] : [],
    producer_information: values.producerInfo ? [{ lang: language, value: values.producerInfo, verified: true }] : [],
    image: imageUrls.length > 0 ? imageUrls[0] : null,
    images: imageUrls.length > 0 ? imageUrls.map(url => ({ url, blurhash: null })) : [],
    menu_id: menuId,
    option_bindings: optionBindingAttrs,
    forbidden_courier_capabilities: [],
    required_courier_capabilities: [],
    external_info_hyperlink:
      (values.isOTCRestricted || settings.externalInfoHyperlinkAllowed) &&
      values.externalInfoHyperlink?.hyperlink &&
      values.externalInfoHyperlink?.text
        ? {
            text: [{ lang: language, value: values.externalInfoHyperlink.text, verified: true }],
            hyperlink: values.externalInfoHyperlink.hyperlink,
          }
        : null,
    restrictions: [
      values.isOTCRestricted ? { type: 'over_the_counter' as const } : undefined,
      values.isAgeRestricted ? { type: 'age_restricted' as const, age_limit: values.ageLimit || null } : undefined,
      values.isDeliveryHandshakeRestricted ? { type: 'delivery_handshake' as const } : undefined,
    ].filter(isDefined),
    return_policy: values?.returnPolicy ?? null,
  };
};

export const transformItemFormValuesToUpdateApi = (
  item: Item,
  values: ValidFormValues,
  imageUrls: string[],
  settings: {
    language: Language;
    nutritionFactsFormat: 'text' | 'table';
    externalInfoHyperlinkAllowed: boolean;
  },
): ItemUpdatePayload => {
  if (item.discounted && item.discounted.original_price.currency !== item.baseprice.currency) {
    throw new Error('baseprice and discounted.original_price are in different currency');
  }

  const { language, nutritionFactsFormat } = settings;

  const baseAttributes = transformItemFormValuesToApiBaseAttrs(values, {
    currency: item.baseprice.currency,
    nutritionFactsFormat,
  });

  // Strip client-side created IDs from newly created option bindings
  const optionBindings: (OptionBindingAttrs | OptionBinding)[] = values.optionBindings?.map(binding => {
    const originalBinding = item.option_bindings.find(itemBinding => itemBinding.id === binding.id);
    if (originalBinding) {
      return binding;
    }

    return pickOptionBindingAttrs(binding);
  });

  return {
    ...baseAttributes,
    dietary_preferences: values.dietaryPreferences,
    description: values.description
      ? updatePrimaryLanguageTranslation(language, values.description, item.description)
      : [],
    name: values.name ? updatePrimaryLanguageTranslation(language, values.name, item.name) : [],
    additives: values.additives ? updatePrimaryLanguageTranslation(language, values.additives, item.additives) : [],
    allergens: values.allergens ? updatePrimaryLanguageTranslation(language, values.allergens, item.allergens) : [],
    conditions_of_use_and_storage: values.usageInstructions
      ? updatePrimaryLanguageTranslation(language, values.usageInstructions, item.conditions_of_use_and_storage)
      : [],
    country_of_origin: values.countryOfOrigin
      ? updatePrimaryLanguageTranslation(language, values.countryOfOrigin, item.country_of_origin)
      : [],
    distributor_information: values.distributorInfo
      ? updatePrimaryLanguageTranslation(language, values.distributorInfo, item.distributor_information)
      : [],
    ingredients: values.ingredients
      ? updatePrimaryLanguageTranslation(language, values.ingredients, item.ingredients)
      : [],
    more_information: values.moreInformation
      ? updatePrimaryLanguageTranslation(language, values.moreInformation, item.more_information)
      : [],
    nutrition_facts:
      nutritionFactsFormat === 'text' && values.nutritionFacts
        ? updatePrimaryLanguageTranslation(language, values.nutritionFacts, item.nutrition_facts)
        : [],
    producer_information: values.producerInfo
      ? updatePrimaryLanguageTranslation(language, values.producerInfo, item.producer_information)
      : [],
    image: imageUrls.length > 0 ? imageUrls[0] : null,
    images: imageUrls.length > 0 ? imageUrls.map(url => ({ url, blurhash: null })) : [],
    forbidden_courier_capabilities: item.forbidden_courier_capabilities,
    required_courier_capabilities: item.required_courier_capabilities,
    menu_id: item.menu_id,
    id: item.id,
    option_bindings: values.weightType === 'weighted-items' ? [] : optionBindings,
    version: item.version,
    external_info_hyperlink:
      (values.isOTCRestricted || settings.externalInfoHyperlinkAllowed) &&
      values.externalInfoHyperlink?.hyperlink &&
      values.externalInfoHyperlink.text
        ? {
            text: updatePrimaryLanguageTranslation(
              language,
              values.externalInfoHyperlink.text,
              item.external_info_hyperlink?.text ?? [],
            ),
            hyperlink: values.externalInfoHyperlink.hyperlink,
          }
        : null,
    restrictions: [
      values.isOTCRestricted ? { type: 'over_the_counter' as const } : undefined,
      values.isAgeRestricted ? { type: 'age_restricted' as const, age_limit: values.ageLimit || null } : undefined,
      values.isDeliveryHandshakeRestricted ? { type: 'delivery_handshake' as const } : undefined,
    ].filter(isDefined),
    return_policy: values?.returnPolicy ?? null,
  };
};

const apiToFormFieldNameMap: Partial<Record<keyof Product, keyof FormValues>> = {
  additives: 'additives',
  brand_id: 'brandId',
  alcohol_percentage: 'alcoholPercentage',
  description: 'description',
  dietary_preferences: 'dietaryPreferences',
  display_unit: 'displayUnit',
  number_of_units: 'numberOfUnits',
  product_hierarchy_level_id: 'productHierarchyLevelId',
  volume_in_ml: 'volumeInMl',
  weight_in_grams: 'weightInGrams',
  allergens: 'allergens',
  conditions_of_use_and_storage: 'usageInstructions',
  country_of_origin: 'countryOfOrigin',
  distributor_information: 'distributorInfo',
  ingredients: 'ingredients',
  mandatory_warnings: 'mandatoryWarnings',
  more_information: 'moreInformation',
  nutrition_facts: 'nutritionFacts',
  nutrition_values_baseline: 'nutritionValuesBaseline',
  nutrition_values: 'nutritionValues',
  producer_information: 'producerInfo',
  name: 'name',
  images: 'image',
};

export const transformApiFieldNamesToFormFieldNames = (fields: (keyof Product)[]): (keyof FormValues)[] =>
  fields.map(field => apiToFormFieldNameMap[field]).filter(isDefined);

const transformFeedProductImagesToFormValues = (product: Product): FeedFormValues['image'] =>
  product.images?.map(imageUrl => ({
    type: 'existing-image',
    imageUrl,
    id: imageUrl,
  })) ?? [];

export const transformFeedProductToFormValues = (
  product: Product,
  options: { language: Language; nutritionSettings: NutritionSetting[] },
): FeedFormValues => {
  const { language, nutritionSettings } = options;
  return {
    additives: findTranslation(product.additives, language),
    alcoholPercentage: product.alcohol_percentage ? Number(product.alcohol_percentage) : 0,
    allergens: findTranslation(product.allergens, language),
    brandId: product.brand_id,
    description: findTranslation(product.description, language),
    dietaryPreferences: product.dietary_preferences,
    distributorInfo: findTranslation(product.distributor_information, language),
    image: transformFeedProductImagesToFormValues(product),
    ingredients: findTranslation(product.ingredients, language),
    mandatoryWarnings: product.mandatory_warnings,
    moreInformation: findTranslation(product.more_information, language),
    name: findTranslation(product.name, language),
    numberOfUnits: product.number_of_units === null ? undefined : product.number_of_units,
    nutritionFacts: findTranslation(product.nutrition_facts, language),
    nutritionValuesBaseline: product.nutrition_values_baseline || undefined,
    nutritionValues: product.nutrition_values
      .map(value => ({ id: value.nutrition_value_id, value: value.amount }))
      .sort(
        (valueA, valueB) =>
          (nutritionSettings.find(setting => setting.id === valueA.id)?.table_row_number || 0) -
          (nutritionSettings.find(setting => setting.id === valueB.id)?.table_row_number || 0),
      ),
    countryOfOrigin: findTranslation(product.country_of_origin || [], language),
    usageInstructions: findTranslation(product.conditions_of_use_and_storage || [], language),
    producerInfo: findTranslation(product.producer_information, language),
    volumeInMl: product.volume_in_ml === null ? undefined : product.volume_in_ml,
    weightInGrams: product.weight_in_grams === null ? undefined : product.weight_in_grams,
    displayUnit: product.display_unit,
    productHierarchyLevelId: product.product_hierarchy_level_id || undefined,
  };
};
